/**
 * QuestionPrompt - UI for answering IT questions
 *
 * Displays a question from IT staff with a form for the user to respond.
 * Uses amber/yellow styling to indicate action required.
 */

import { useState, useCallback } from 'react';
import './QuestionPrompt.css';

interface QuestionPromptProps {
  questionId: string;
  questionText: string;
  authorName: string;
  createdAt: Date;
  onSubmitAnswer: (questionId: string, answer: string) => Promise<void>;
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

export function QuestionPrompt({
  questionId,
  questionText,
  authorName,
  createdAt,
  onSubmitAnswer,
}: QuestionPromptProps) {
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!answer.trim()) {
      setError('Please enter your response');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmitAnswer(questionId, answer.trim());
      setAnswer('');
    } catch (err) {
      console.error('Failed to submit answer:', err);
      setError('Failed to submit response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [answer, questionId, onSubmitAnswer]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && answer.trim() && !isSubmitting) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [answer, isSubmitting, handleSubmit]
  );

  return (
    <div className="question-prompt">
      <div className="question-prompt__header">
        <div className="question-prompt__icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 17H9v-2h2v2zm2.07-7.75l-.9.92C11.45 10.9 11 11.5 11 13H9v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H6c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
          </svg>
        </div>
        <div className="question-prompt__meta">
          <div className="question-prompt__author-row">
            <span className="question-prompt__author">{authorName} asked a question</span>
            <span className="question-prompt__time">{formatRelativeTime(createdAt)}</span>
          </div>
          <span className="question-prompt__badge">Response needed</span>
        </div>
      </div>

      <div className="question-prompt__question">
        <p>{questionText}</p>
      </div>

      <div className="question-prompt__answer-section">
        {error && (
          <div className="question-prompt__error" role="alert">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 0a8 8 0 100 16A8 8 0 008 0zM7 4h2v5H7V4zm0 6h2v2H7v-2z" />
            </svg>
            {error}
          </div>
        )}
        <textarea
          className="question-prompt__textarea"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your response here..."
          rows={3}
          disabled={isSubmitting}
          aria-label="Your response to the question"
        />
        <div className="question-prompt__actions">
          <span className="question-prompt__hint">Ctrl+Enter to submit</span>
          <button
            type="button"
            className="question-prompt__submit"
            onClick={handleSubmit}
            disabled={isSubmitting || !answer.trim()}
          >
            {isSubmitting ? (
              <>
                <span className="question-prompt__spinner" />
                Sending...
              </>
            ) : (
              'Submit Response'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
